
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { environment } from "../../../environments/environment";
import moment from "moment";
import _ from "lodash";
import md5 from "blueimp-md5";
import { Observable, Subject } from "rxjs";
import { take } from "rxjs/operators";
import { StorageService } from "../services/storage.service";

export function mobileonly(target: Object, method: string, descriptor: PropertyDescriptor) {
  let originalMethod = descriptor.value;
  descriptor.value = function(...args){
    if (!CommonUtil.isOnNativeMobileDevice()) {
      return;
    }
    return originalMethod.apply(this, args);
  };
}

const EMAIL_REGEXP =
  /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

export class CommonUtil {
  // PRASHANT_COMMENT common helper methods here
  static imagesExtensions = ["jpeg", "jpg", "gif", "png", "bmp", "tiff", "webp", "svg"];
  static videoExtensions = ["mp4", "mov", "wmv", "webm", "wma", "flv", "mov", "mpg", "avi", "3gp", "mkv"];

  static audioExtensions = ["mp3", "wav"];

  static documentExtensions = ["csv",
    "txt",
    "xls",
    "xlsx",
    "doc",
    "docx",
    "html",
    "js",
    "pdf",
    "pptx",
    "ppt",
    "ods",
    "odt",
    "excel"
  ];

  // Trusted VNC hosts used for postMessage, redirect and app switcher validation.
  static signupUrl = "https://signup.vncmeet.com";
  static allowedAppDomains = ["vnc.biz", "vnclagoon.com", "vncmeet.com", "vnctask.com"];
  static allowedNativeSchemes = ["vnctalk", "vncmail", "vnctask", "vncproject", "vnccontacts", "vnccalendar", "vncmcb"];

  static randomId(length: number = 7, containNumbers: boolean = true) {
    let randomId = "";
    let dictionary = "abcdefghijklmnopqrstuvwxyz";

    if (containNumbers) {
      dictionary += "0123456789";
    }

    const dictionaryLength = dictionary.length;

    for (let i = 0; i < length; i++) {
      randomId += dictionary.charAt(Math.floor(Math.random() * dictionaryLength));
    }

    return randomId;
  }

  static getRandomNumbers() {
    let array = new Uint32Array(10);
    window.crypto.getRandomValues(array);
    return array.join("");
  }

  /**
   * Key for a queued offline operation.
   *
   * These used to be Math.floor(Math.random() * 1000), which collides roughly once
   * in a thousand - and because the pending store uses keyPath "id" with a put(),
   * a collision silently overwrote the earlier queued edit. A UUID removes the
   * collision entirely. The store is not autoIncrement, so string keys need no
   * schema change and existing numeric-keyed entries drain alongside these.
   */
  static newPendingOperationId(): string {
    const cryptoRef: any = window.crypto;
    if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
      return cryptoRef.randomUUID();
    }
    // randomUUID needs a secure context; fall back to random bytes rather than Math.random.
    const bytes = new Uint8Array(16);
    cryptoRef.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10x
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  static translateAppURLtoDeeplinkURL(url: string): string {
    let origURL = new URL(url);
    let deepLinkUrl = origURL.protocol + "//" + origURL.hostname + "/api/deeplink" + origURL.pathname + origURL.search;
    return deepLinkUrl;
  }

  static escapeHTMLString(unescapedString: string): string {
    return unescapedString
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  static escapeCarriagesAndNewline(unescapedString: string): string {
    return unescapedString.replace(/\r?\n/g, " ");
  }

  // Escapes a matched URL/email before it is emitted into anchor markup, so a
  // crafted link can never break out of the href attribute or inject new tags.
  private static escapeLinkPart(part: string): string {
    return CommonUtil.escapeHTMLString(part)
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  static linkify(inputText: string): string {
    let replacedText, replacePattern1, replacePattern2, replacePattern3;

    // URLs starting with http://, https://, or ftp://
    replacePattern1 = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
    replacedText = inputText.replace(replacePattern1, (match) => {
      const url = CommonUtil.escapeLinkPart(match);
      return `<a href="${url}" target="_blank" class="open-new-window">${url}</a>`;
    });

    // URLs starting with "www." (without // before it, or it"d re-link the ones done above).
    replacePattern2 = /(^|[^\/])(www\.[-a-zA-Z0-9@:%._\+~#=]+(\b|$))/gim;
    replacedText = replacedText.replace(replacePattern2, (match, prefix, url) => {
      const escapedUrl = CommonUtil.escapeLinkPart(url);
      return `${prefix}<a href="http://${escapedUrl}" target="_blank" class="open-new-window">${escapedUrl}</a>`;
    });

    // Change email addresses to mailto:: links.
    replacePattern3 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim;
    replacedText = replacedText.replace(replacePattern3, (match) => {
      const email = CommonUtil.escapeLinkPart(match);
      return `<a href="mailto:${email}">${email}</a>`;
    });

    return replacedText;
  }

  static escapeColonURLs(inputText: string): string {
    let replacedText, replacePattern1, replacePattern2;

    // URLs starting with http://, https://, or ftp://
    replacePattern1 = /\"(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])\":/gim;
    replacedText = inputText.replace(replacePattern1, "");

    replacePattern2 = /(^|[^\/])\"(www\.[\S]+)\":/gim;
    replacedText = replacedText.replace(replacePattern2, "");

    return replacedText;
  }

  static validateEmail(email) {
    // RFC822 version
    let sQtext = "[^\\x0d\\x22\\x5c\\x80-\\xff]";
    let sDtext = "[^\\x0d\\x5b-\\x5d\\x80-\\xff]";
    let sAtom = "[^\\x00-\\x20\\x22\\x28\\x29\\x2c\\x2e\\x3a-\\x3c\\x3e\\x40\\x5b-\\x5d\\x7f-\\xff]+";
    let sQuotedPair = "\\x5c[\\x00-\\x7f]";
    let sDomainLiteral = "\\x5b(" + sDtext + "|" + sQuotedPair + ")*\\x5d";
    let sQuotedString = "\\x22(" + sQtext + "|" + sQuotedPair + ")*\\x22";
    let sDomain_ref = sAtom;
    let sSubDomain = "(" + sDomain_ref + "|" + sDomainLiteral + ")";
    let sWord = "(" + sAtom + "|" + sQuotedString + ")";
    let sDomain = sSubDomain + "(\\x2e" + sSubDomain + ")*";
    let sLocalPart = sWord + "(\\x2e" + sWord + ")*";
    let sAddrSpec = sLocalPart + "\\x40" + sDomain; // complete RFC822 email address spec
    let sValidEmail = "^" + sAddrSpec + "$"; // as whole string

    let reValidEmail = new RegExp(sValidEmail);

    return reValidEmail.test(email);
  }

  static logger(data: any) { }

  static isOnMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|PlayBook/i
      .test(navigator.userAgent);
  }

  static isOnIOS() {
    // return typeof device !== "undefined" && device.platform.toUpperCase() === "IOS";
    return window.location.href.startsWith("app://");
  }

  static isOnAndroid() {
    return typeof device !== "undefined" && device.platform.toUpperCase() === "ANDROID";
    // return window.location.href.startsWith("file://");
  }

  static isMobileSize() {
    return window.outerWidth < 768;
  }

  static isOnNativeMobileDevice(){
    return CommonUtil.isOnIOS() || CommonUtil.isOnAndroid();
  }

  static isInsideIFrame() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  static isOnIpad() {
    return /iPad/i
      .test(navigator.userAgent);
  }

  static getBaseUrl() {
    const baseUrl = window.location.href;

    if (environment.isCordova) {
        return CommonUtil.isOnAndroid() && baseUrl.startsWith("http") ? "" : baseUrl.split("/task")[0].replace(/index.html/gi, "");;
    } else if (environment.isElectron) {
        return baseUrl.includes("/index.html") ? baseUrl.split("/index.html")[0] : baseUrl.split("/task")[0];
    } else {
        return "";
    }
  }

  static getFullUrl(url: string) {
    return CommonUtil.getBaseUrl() + url;
  }

  // Origins we accept window "message" events from: our own page, the configured
  // backend server and the VNC signup host loaded by the registration iframes.
  static getTrustedMessageOrigins(): string[] {
    const origins: string[] = [];
    if (window.location && window.location.origin && window.location.origin !== "null") {
      origins.push(window.location.origin);
    }
    const serverURL = StorageService.getLocalItem("serverURL");
    [serverURL, CommonUtil.signupUrl].forEach(url => {
      if (url && typeof url === "string") {
        try {
          origins.push(new URL(url.trim()).origin);
        } catch (e) {
          // ignore malformed configuration
        }
      }
    });
    return origins;
  }

  static isTrustedMessageEvent(event: MessageEvent): boolean {
    const origin = event ? event.origin : null;
    // Cordova/Electron builds run from file:// (or a custom scheme) where the origin
    // of a same-document message is reported as "null" or an empty string.
    if (!origin || origin === "null") {
      return environment.isCordova || environment.isElectron;
    }
    return CommonUtil.getTrustedMessageOrigins().indexOf(origin) > -1;
  }

  // Returns the url only when it stays inside this app, otherwise null.
  static getSafeInternalRedirectUrl(url: string): string {
    if (!url || typeof url !== "string") {
      return null;
    }
    const value = url.trim();
    // Scheme relative ("//evil.com") and backslash variants resolve to another host.
    if (!value || value.startsWith("//") || value.startsWith("\\") || value.startsWith("/\\")) {
      return null;
    }
    let parsed: URL;
    try {
      parsed = new URL(value, window.location.href);
    } catch (e) {
      return null;
    }
    if (parsed.protocol !== window.location.protocol) {
      return null;
    }
    // file:// urls have an opaque ("null") origin, compare the host instead.
    if (window.location.origin && window.location.origin !== "null") {
      if (parsed.origin !== window.location.origin) {
        return null;
      }
    } else if (parsed.host !== window.location.host) {
      return null;
    }
    return parsed.href;
  }

  // Hosts the app switcher is allowed to navigate to.
  static isAllowedAppUrl(url: string): boolean {
    if (!url || typeof url !== "string") {
      return false;
    }
    let parsed: URL;
    try {
      parsed = new URL(url.trim(), window.location.href);
    } catch (e) {
      return false;
    }
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
      return false;
    }
    const host = parsed.hostname.toLowerCase();
    if (!host) {
      return false;
    }
    if (window.location.hostname && host === window.location.hostname.toLowerCase()) {
      return true;
    }
    const serverURL = StorageService.getLocalItem("serverURL");
    if (serverURL && typeof serverURL === "string") {
      try {
        if (host === new URL(serverURL.trim()).hostname.toLowerCase()) {
          return true;
        }
      } catch (e) {
        // ignore malformed configuration
      }
    }
    return CommonUtil.allowedAppDomains.some(domain => host === domain || host.endsWith("." + domain));
  }

  static isAllowedNativeLink(link: string): boolean {
    if (!link || typeof link !== "string") {
      return false;
    }
    const scheme = link.trim().toLowerCase().split(":")[0];
    return CommonUtil.allowedNativeSchemes.indexOf(scheme) > -1;
  }

  static isAudio(fileType: string): boolean {
    return this.audioExtensions.indexOf(fileType.toLowerCase()) !== -1;
  }

  static isImage(fileType: string): boolean {
    return this.imagesExtensions.indexOf(fileType.toLowerCase()) !== -1;
  }

  static isVideo(fileType: string): boolean {
     return this.videoExtensions.indexOf(fileType.toLowerCase()) !== -1;
  }

  static isDocument(fileType: string): boolean {
    if (!fileType || (typeof fileType !== "string")) {
      return false;
    }
    return this.documentExtensions.indexOf(fileType?.toLowerCase()) !== -1;
  }

  // Extensions accepted for attachment upload. Client-side validation is advisory
  // only - the server must enforce the same list.
  static allowedAttachmentExtensions = [
    ...CommonUtil.imagesExtensions,
    ...CommonUtil.videoExtensions,
    ...CommonUtil.audioExtensions,
    ...CommonUtil.documentExtensions,
    "aac", "amr", "aiff", "flac", "ogg", "oog", "m4a",
    "rtf", "odp", "ods", "odt", "zip", "json", "xml", "md", "log"
  ];

  // MIME types never accepted, whatever the extension says.
  static blockedAttachmentContentTypes = [
    "application/x-msdownload",
    "application/x-msdos-program",
    "application/x-executable",
    "application/x-sh",
    "application/x-shellscript",
    "application/vnd.microsoft.portable-executable",
    "application/x-httpd-php",
    "text/x-php"
  ];

  static getFileExtension(filename: string): string {
    if (!filename || typeof filename !== "string") {
      return "";
    }
    const parts = filename.split(".");
    return parts.length > 1 ? parts.pop().toLowerCase().trim() : "";
  }

  static isAllowedAttachment(filename: string, contentType?: string): boolean {
    const extension = CommonUtil.getFileExtension(filename);
    if (!extension || CommonUtil.allowedAttachmentExtensions.indexOf(extension) === -1) {
      return false;
    }
    if (contentType && CommonUtil.blockedAttachmentContentTypes.indexOf(contentType.toLowerCase().split(";")[0].trim()) > -1) {
      return false;
    }
    return true;
  }

  static getMediaType(body: string): string {
    if (body) {
      body = body.toLowerCase();
    }

    if ((body.lastIndexOf("aac") === body.length - 3) ||
      (body.lastIndexOf("amr") === body.length - 3) ||
      (body.lastIndexOf("aiff") === body.length - 4) ||
      (body.lastIndexOf("flac") === body.length - 4) ||
      (body.lastIndexOf("mp3") === body.length - 3) ||
      (body.lastIndexOf("oog") === body.length - 3) ||
      (body.lastIndexOf("wma") === body.length - 3) ||
      (body.lastIndexOf("wav") === body.length - 3)) {
      return MediaType.VOICE_MESSAGE;
    }

    if ((body.lastIndexOf("jpeg") === body.length - 4) ||
      (body.lastIndexOf("jpg") === body.length - 3) ||
      (body.lastIndexOf("gif") === body.length - 3) ||
      (body.lastIndexOf("webp") === body.length - 4) ||
      (body.lastIndexOf("png") === body.length - 3) ||
      (body.lastIndexOf("PNG") === body.length - 3) ||
      (body.lastIndexOf("bmp") === body.length - 3) ||
      (body.lastIndexOf("svg") === body.length - 3) ||
      (body.lastIndexOf("tiff") === body.length - 4) ||
      (body.lastIndexOf("ico") === body.length - 3) ||
      (body.lastIndexOf("odi") === body.length - 3)) {
      return MediaType.IMAGE;
    }

    if ((body.lastIndexOf("3gp") === body.length - 3) ||
      (body.lastIndexOf("avi") === body.length - 3) ||
      (body.lastIndexOf("flv") === body.length - 3) ||
      (body.lastIndexOf("m4v") === body.length - 3) ||
      (body.lastIndexOf("mkv") === body.length - 3) ||
      (body.lastIndexOf("mov") === body.length - 3) ||
      (body.lastIndexOf("mp4") === body.length - 3) ||
      (body.lastIndexOf("mpeg") === body.length - 4) ||
      (body.lastIndexOf("mpg") === body.length - 3) ||
      (body.lastIndexOf("mts") === body.length - 3) ||
      (body.lastIndexOf("rm") === body.length - 2) ||
      (body.lastIndexOf("vob") === body.length - 3) ||
      (body.lastIndexOf("quicktime") === body.length - 9) ||
      (body.lastIndexOf("wmv") === body.length - 3)) {
      return MediaType.VIDEOS;
    }

    if (body.lastIndexOf("pdf") === body.length - 3) {
      return MediaType.PDF;
    }

    if ((body.lastIndexOf("txt") === body.length - 3) ||
      (body.lastIndexOf("rtf") === body.length - 3) ||
      (body.lastIndexOf("dat") === body.length - 3)) {
      return MediaType.TEXT;
    }

    if ((body.lastIndexOf("doc") === body.length - 3) ||
      (body.lastIndexOf("docx") === body.length - 4) ||
      (body.lastIndexOf("docm") === body.length - 4) ||
      (body.lastIndexOf("odt") === body.length - 3) ||
      (body.lastIndexOf("html") === body.length - 4) ||
      (body.lastIndexOf("js") === body.length - 2)) {
      return MediaType.DOC;
    }

    if ((body.lastIndexOf("xls") === body.length - 3) ||
      (body.lastIndexOf("xlr") === body.length - 3) ||
      (body.lastIndexOf("xlsx") === body.length - 4) ||
      (body.lastIndexOf("xlsm") === body.length - 3) ||
      (body.lastIndexOf("ods") === body.length - 3) ||
      (body.lastIndexOf("csv") === body.length - 3) ||
      (body.lastIndexOf("tsv") === body.length - 3)) {
      return MediaType.EXCEL;
    }

    if ((body.lastIndexOf("ppt") === body.length - 3) ||
      (body.lastIndexOf("pptx") === body.length - 4) ||
      (body.lastIndexOf("pps") === body.length - 3) ||
      (body.lastIndexOf("odp") === body.length - 3)) {
      return MediaType.PPT;
    }

    if ((body.lastIndexOf("rar") === body.length - 3) ||
      (body.lastIndexOf("tar") === body.length - 3)) {
      return MediaType.RAR;
    }

    if ((body.lastIndexOf("zip") === body.length - 3) ||
      (body.lastIndexOf("7z") === body.length - 2)) {
      return MediaType.ZIP;
    }

    return MediaType.ATTACHMENTS;
  }

  static getMediaUrl(type: string): string {
    let url = "";

    if (type === MediaType.VOICE_MESSAGE) {
      url = "/assets/media-icons/file-document-mic.svg";
    }
    if (type === MediaType.VIDEOS) {
      url = "/assets/media-icons/file-document-video.svg";
    }
    if (type === MediaType.IMAGE) {
      url = "/assets/media-icons/file-document-image.svg";
    }
    if (type === MediaType.PDF) {
      url = "/assets/media-icons/file-pdf-box.svg";
    }
    if (type === MediaType.DOC) {
      url = "/assets/media-icons/icons8-microsoft-word.svg";
    }
    if (type === MediaType.RAR || type === MediaType.ZIP) {
      url = "/assets/media-icons/zip-box.svg";
    }
    if (type === MediaType.EXCEL) {
      url = "/assets/media-icons/icons8-microsoft-excel.svg";
    }
    if (type === MediaType.TEXT) {
      url = "/assets/media-icons/file-document-box.svg";
    }
    if (type === MediaType.PPT) {
      url = "/assets/media-icons/icons8-microsoft-powerpoint.svg";
    }
    if (type === MediaType.ATTACHMENTS) {
      url = "/assets/media-icons/file-document-box.svg";
    }

    return this.getFullUrl(url);
  }

  static parseRedmineMentions(prefix: string, text: string): string[] {
    if (!text) {
      return [];
    }
    const mentionsRegex = new RegExp(prefix + "#([a-zA-Z0-9\_\.\-]+@[a-zA-Z0-9\_\.\-]+)", "gim");
    // LoggerService.info("[parseMentions]", mentionsRegex, text);
    let matches = text.match(mentionsRegex);
    if (matches && matches.length) {
        return matches.filter(v => CommonUtil.isValidEmail(v));
    } else {
        return [];
    }
  }

    static isValidEmail(email: string): boolean {
    if (email.endsWith(".")) {
      email = email.slice(0, email.length - 1);
    }
    return EMAIL_REGEXP.test(email);
  }

  static trimString(subject: string, length: number, suffix: string = "...") {
    if (subject.length <= length) {
      return subject;
    }

    const suffixLength = suffix.length;
    const trimLength = length - suffixLength;

    return subject.slice(0, trimLength - 1) + suffix;
  }

  static isToday(momentDate) {
    let yesterday = moment().clone().startOf("day");
    return momentDate.isSame(yesterday , "d");
  }

  static isYesterday(momentDate) {
    let yesterday = moment().clone().subtract(1, "days").startOf("day");
    return momentDate.isSame(yesterday , "d");
  }

  static getTimeAgo(timestamp) {
    return moment(timestamp).fromNow();
  }

  static getTimeAgoWithLocale(timestamp, locale) {
    moment.locale(locale);
    return moment(timestamp).fromNow();
  }

  static hideBackdrop() {
    if (document.querySelector(".cdk-overlay-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".cdk-overlay-backdrop")).style.display = "none";
    }
    if (CommonUtil.isOnMobileDevice() && document.querySelector(".dialog-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".dialog-backdrop")).style.display = "none";
    }
  }

  static showBackdrop() {
    if (document.querySelector(".cdk-overlay-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".cdk-overlay-backdrop")).style.display = "block";
    }
    if (CommonUtil.isOnMobileDevice() && document.querySelector(".dialog-backdrop") !== null) {
      (<HTMLElement>document.querySelector(".dialog-backdrop")).style.display = "block";
    }
  }

  static getIcon(appName: string): string {
    let icon = "";
    let name = appName.replace("vnc", "VNC");
    switch (appName.toLowerCase()) {
      case "vncmail": icon = CommonUtil.getFullUrl("/assets/images/VNCmail.svg"); break;
      case "vnctask": icon = CommonUtil.getFullUrl("/assets/images/VNCtask.svg"); break;
      case "vncmcb": icon = CommonUtil.getFullUrl("/assets/images/VNCmcb.svg"); break;
      case "vnccontacts": icon = CommonUtil.getFullUrl("/assets/images/VNCcontacts.svg"); break;
      case "vnctalk": icon = CommonUtil.getFullUrl("/assets/images/VNCtalk.svg"); break;
      case "vnccalendar": icon = CommonUtil.getFullUrl("/assets/images/VNCcalendar.svg"); break;
      case "vncproject": icon = CommonUtil.getFullUrl("/assets/images/VNCproject.svg"); break;
      default: icon = CommonUtil.getFullUrl("/assets/images/" + name + ".svg"); break;
    }
    return icon;
  }

  static getPackageName(appName: string): string {
    const packageName = `biz.vnc.${appName.toLowerCase()}`;
    //console.log("getPackageName", appName, packageName);
    return packageName;
  }

  static getAppUrl(appName: string): string {
    const availableiOSApps = {
      "vncmail": "",
      "vnctask": "itms-apps://itunes.apple.com/app/id1423089930",
      "vnccontacts": ""
    };
    const availableAndroidApps = {
      "vncmail": "",
      "vnctask": "market://details?id=biz.vnc.vnctask",
      "vnccontacts": ""
    };
    if (!CommonUtil.isOnAndroid()) {
      return availableiOSApps[appName.toLowerCase()];
    }
    return availableAndroidApps[appName.toLowerCase()];
  }

  static strip_html_tags(str: string): string {
    if ((str === null) || (str === "")) {
      return str;
    } else {
      return str.replace(/<[^>]*>/g, "");
    }
  }

  static parseMentions(text: string): string[] {
    if (!text) {
      return [];
    }
    const mentionsRegex = new RegExp("@([a-zA-Z0-9\_\.\-]+[@]*[a-zA-Z0-9\_\.\-]+)", "gim");
    const matches = text.match(mentionsRegex);
    if (matches && matches.length) {
        return _.uniq(matches.map((match) => {
            return match.slice(1);
        }));
    } else {
        return [];
    }
  }

  static openUrl(url) {
    if (url) {
      let a = document.createElement("a");
      document.body.appendChild(a);
      a.setAttribute("style", "display: none");
      a.setAttribute("class", "open-new-window");
      a.setAttribute("target", "_blank");
      a.href = url;
      a.click();
      a.remove();
    }
  }

  static isSQLSupported() {
    return this.isOnNativeMobileDevice() || environment.isElectron;
    // return this.isOnNativeMobileDevice();
  }

  static getAvatarText(text: string): string {
    if (!text) {
      return "";
    }
    text = text.split("@")[0].replace(/\./g, " ").replace(/\W-_+/g, "");
    const str = text.split(/\s+/);
    if (str.length > 1) {
      return str[0].substr(0, 1).toUpperCase() + str[1].substr(0, 1).toUpperCase();
    }
    return text.substr(0, 2).toUpperCase();
  }


  static getBgColor(text: string): string {
    let color;
    switch (text) {
      case "A":
      case "Q":
        color = "#f44336";
        break;
      case "B":
      case "R":
        color = "#e91e63";
        break;
      case "C":
      case "S":
        color = "#9c27b0";
        break;
      case "D":
      case "T":
        color = "#673ab7";
        break;
      case "E":
      case "U":
        color = "#3f51b5";
        break;
      case "F":
      case "V":
        color = "#2196f3";
        break;
      case "G":
      case "W":
        color = "#03a9f4";
        break;
      case "H":
      case "X":
        color = "#00bcd4";
        break;
      case "I":
      case "Y":
        color = "#009688";
        break;
      case "J":
      case "Z":
        color = "#4caf50";
        break;
      case "K":
        color = "#8bc34a";
        break;
      case "L":
        color = "#ff9800";
        break;
      case "M":
        color = "#ff5722";
        break;
      case "N":
        color = "#795548";
        break;
      case "O":
        color = "#9e9e9e";
        break;
      case "P":
        color = "#607d8b";
        break;
      default:
        color = "#888888";
        break;
    }
    return color;
  }

  static buildTargetHash(target) {
    return md5(target);
  }

  static isFileSystemUrl(url: string) {
    return url.includes("file://");
  }

  static getRandomAvatarColor() {
    const colors = this.getAvatarbgColors();
    const random = Math.floor(Math.random() * colors.length);
    return colors[random];
  }

  static getAvatarbgColors() {
    return [
      "#F44336",
      "#E91E63",
      "#9C27B0",
      "#673AB7",
      "#3F51B5",
      "#2196F3",
      "#03A9F4",
      "#00BCD4",
      "#009688",
      "#4CAF50",
      "#8BC34A",
      "#FF9800",
      "#FF5722",
      "#795548",
      "#9E9E9E",
      "#607D8B"
    ];
  }

  static getCharacters(firstName, lastName) {
    if (firstName && firstName !== undefined && lastName && lastName !== undefined) {
      const fName: string = firstName.trim();
      const lName: string = lastName.trim();
      const chr1 = fName.length > 0 ? fName.charAt(0) : "";
      const chr2 = lName.length > 0 ? lName.charAt(0) : "";
      return (chr1 + chr2).trim();
    } else if (firstName && firstName !== undefined) {
      const fName: string = firstName.trim();
      const chr1 = fName.length > 0 ? fName.charAt(0) : "";
      return chr1;
    } else if (lastName && lastName !== undefined) {
      const lName: string = lastName.trim();
      const chr2 = lName.length > 0 ? lName.charAt(0) : "";
      return chr2;
    } else {
      return "NA";
    }
  }
  static getBase6ImageFromUrl(url) {
    const subject = new Subject();
    const image: HTMLImageElement = new Image();
    image.src = url;
    image.crossOrigin = "Anonymous";
    image.onload = () => {
      const canvas: HTMLCanvasElement = document.createElement("canvas");
      const context: CanvasRenderingContext2D = canvas.getContext("2d");
      canvas.height = image.naturalHeight;
      canvas.width = image.naturalWidth;
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      const b64url = canvas.toDataURL();
      subject.next(b64url);
    };
    image.onerror = (error) => {
      subject.error(error);
    };
    return subject.asObservable().pipe(take(1));
  }

  static lightOrDark(color) {
        if (color === "cyan" || color === "yellow" || color === "pink" || color === "orange") {
            return "light";
        }
        // Variables for red, green, blue values
        let r, g, b, hsp;

        // Check the format of the color, HEX or RGB?
        if (color.match(/^rgb/)) {
            // If HEX --> store the red, green, blue values in separate variables
            color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);
            r = color[1];
            g = color[2];
            b = color[3];
        } else {
            // If RGB --> Convert it to HEX: http://gist.github.com/983661
            color = +("0x" + color.slice(1).replace(
            color.length < 5 && /./g, "$&$&"));
            r = color >> 16;
            g = color >> 8 & 255;
            b = color & 255;
        }

        // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
        hsp = Math.sqrt(
            0.299 * (r * r) +
            0.587 * (g * g) +
            0.114 * (b * b)
        );

        // Using the HSP value, determine whether the color is light or dark
        if (hsp > 127.5) {
            return "light";
        } else {
            return "dark";
        }
    }

  static copyToClipboard(stringArray: string[]) {
    let str = stringArray.join("\n");
    if (environment.isCordova) {
      cordova.plugins.clipboard.copy(str);
    } else {
      (window.Clipboard as any) = (function (window, document, navigator) {
        let textArea,
          copy;

        function isIOS() {
          return navigator.userAgent.match(/ipad|iphone/i);
        }

        function createTextArea(text) {
          textArea = document.createElement("textArea");
          textArea.value = text;
          document.body.appendChild(textArea);
        }

        function selectText() {
          let range,
            selection;

          if (isIOS()) {
            range = document.createRange();
            range.selectNodeContents(textArea);
            selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
            textArea.setSelectionRange(0, 999999);
          } else {
            textArea.select();
          }
        }

        function copyToClipboard() {
          document.execCommand("copy");
          document.body.removeChild(textArea);
        }

        copy = function (text) {
          createTextArea(text);
          selectText();
          copyToClipboard();
          textArea = null;
        };

        return {
          copy: copy
        };
      })(window, document, navigator);
      (window.Clipboard as any).copy(str);
    }
  }

  static copyFileFromUrlToClipboard(url: string): Observable<void> {
    const subject = new Subject<void>();
    if (!document.hasFocus()) {
      subject.error("Document not focused");
    }
    if (!navigator.clipboard || !window.ClipboardItem) {
      subject.error("Clipboard API not supported in this browser.");
    }

    const image: HTMLImageElement = new Image();
    image.src = url;
    // image.crossOrigin = "Anonymous";

    image.onload = () => {
      const canvasElementName = "canvas-" + url;
      const canvas: HTMLCanvasElement = document.createElement("canvas");
      const context: CanvasRenderingContext2D = canvas.getContext("2d");
      canvas.height = image.naturalHeight;
      canvas.width = image.naturalWidth;
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(async (blob) => {
        try {
          if (!document.hasFocus()) {
            window.focus();
          }
          await navigator.clipboard.write([
            new ClipboardItem({
              [blob.type]: blob,
            }),
          ]);
          subject.next();
        } catch (error) {
          console.error("copyToClipboard error: ", error);
          subject.error(error);
        }
      });
    };
    image.onerror = (error) => {
      subject.error(error);
    };

    return subject.asObservable().pipe(take(1));
  }
}

export enum MediaType {
  IMAGE = "image",
  VOICE_MESSAGE = "voice-messages",
  ATTACHMENTS = "attachments",
  VIDEOS = "videos",
  ALL = "all",
  PDF = "pdf",
  DOC = "doc",
  TEXT = "txt",
  EXCEL = "excel",
  PPT = "ppt",
  RAR = "rar",
  ZIP = "zip"
}

export enum MediaLongPressAction {
  VIEW = "view",
  SHARE = "share",
  DOWNLOAD = "download",
  DELETE = "delete"
}
